import React from 'react';
import './InstructionPage.css';
import { useLocation, useNavigate } from 'react-router-dom';

const translations = {
    english: {
        welcome: "You are starting the assessment for an activity of the type:",
        instructionsTitle: "Instructions for the assessment:",
        instructionIntroduction: 'Please note that you must complete this assessment for each activity individually (for each plant, product type/technology, building, or instrument/device), as both component-specific and location-specific information is required. If you have several activities of the same type, please carry out this assessment several times.',
        instructions: `
            <ul>
                <li>Do not refresh the page.</li>
                <li>You can use the <strong>&quot;Previous&quot;</strong> and <strong>&quot;Next&quot;</strong> buttons to navigate between questions.</li>
                <li>An evaluation dashboard will be created after you finish the assessment by clicking the save button at the end of the assessment.</li>
                <li>In case of an expert check or 3rd party verification, you’ll be urged at a later step to upload documents to prove your inputs.</li>
                <li>Be aware that you have to complete the assessment for the Minimum Social Safeguards (MSS) before you’ll be able to proceed to the expert check.</li>
            </ul>`,
        cancelButton: "Cancel",
        startButton: "Start Assessment"
    },
    german: {
        welcome: "Sie beginnen die Bewertung für eine Aktivität des Typs:",
        instructionsTitle: "Anweisungen für die Bewertung:",
        instructionIntroduction: 'Bitte beachten Sie, dass Sie diese Bewertung für jede Aktivität einzeln durchführen müssen (für jede Anlage, Produkttyp/Technologie, Gebäude oder Instrument/Gerät), da sowohl komponentenspezifische als auch standortspezifische Informationen erforderlich sind. Wenn Sie mehrere Aktivitäten desselben Typs haben, führen Sie diese Bewertung bitte mehrfach durch.',
        instructions: `
            <ul>
                <li>Aktualisieren Sie die Seite nicht.</li>
                <li>Sie können die <strong>&quot;Zurück&quot;</strong> und <strong>&quot;Weiter&quot;</strong> Tasten verwenden, um zwischen den Fragen zu navigieren.</li>
                <li>Das Auswertungs-Dashboard wird erstellt, nachdem Sie die Bewertung abgeschlossen haben, indem Sie am Ende der Bewertung auf "Speichern" klicken.</li>
                <li>Im Falle der Expertenprüfung oder der Überprüfung durch Dritte werden Sie in einem späteren Schritt aufgefordert, Dokumente hochzuladen, um Ihre Angaben zu belegen.</li>
                <li>Bitte beachten Sie, dass Sie die Bewertung der sozialen Mindeststandards (Minimum Social Safeguards - MSS) durchführen müssen, bevor Sie zur Expertenprüfung übergehen können.</li>
            </ul>`,
        cancelButton: "Abbrechen",
        startButton: "Assessment starten"
    },
    italian: {
        welcome: "Stai iniziando la valutazione per un'attività del tipo:",
        instructionsTitle: "Istruzioni per la valutazione:",
        instructionIntroduction: 'Si prega di notare che è necessario completare questa valutazione per ciascuna attività individualmente (per ciascun impianto, tipo di prodotto/tecnologia, edificio o strumento/dispositivo), poiché sono richieste informazioni sia specifiche per il componente che per la localizzazione. Se hai diverse attività dello stesso tipo, esegui questa valutazione più volte.',
        instructions: `
            <ul>
                <li>Non aggiornare la pagina.</li>
                <li>Puoi usare i pulsanti <strong>&quot;Precedente&quot;</strong> e <strong>&quot;Successivo&quot;</strong> per navigare tra le domande.</li>
                <li>Una dashboard di valutazione verrà creata dopo che avrai terminato la valutazione cliccando sul pulsante di salvataggio alla fine della valutazione.</li>
                <li>In caso di controllo da parte di esperti o verifica da parte di terzi, ti verrà chiesto in una fase successiva di caricare documenti per comprovare i tuoi inserimenti.</li>
                <li>Si prega di notare che è necessario completare la valutazione per le Salvaguardie Sociali Minime (MSS) prima di poter procedere al controllo degli esperti.</li>
            </ul>`,
        cancelButton: "Annulla",
        startButton: "Inizia Valutazione"
    },
czech: {
    welcome: "Začínáte hodnocení pro aktivitu tohoto typu:",
    instructionsTitle: "Pokyny pro hodnocení:",
    instructionIntroduction: 'Všimněte si, že je nutné dokončit toto hodnocení pro každou jednotlivou aktivitu (pro každý závod, typ produktu/technologie, budovu nebo nástroj/přístroj), protože jsou požadovány informace jak specifické pro komponent, tak pro lokalizaci. Pokud máte více aktivit stejného typu, proveďte toto hodnocení několikrát.',
    instructions: `
        <ul>
            <li>Neaktualizujte stránku.</li>
            <li>Pro navigaci mezi otázkami můžete použít tlačítka <strong>"Předchozí"</strong> a <strong>"Další"</strong>.</li>
            <li>Po dokončení hodnocení a kliknutí na tlačítko pro uložení na konci hodnocení bude vytvořena hodnotící dashboard.</li>
            <li>V případě kontroly odborníky nebo verifikace třetí stranou budete v pozdější fázi požádáni o nahrání dokumentů, které potvrzují vaše zadání.</li>
            <li>Všimněte si, že před pokračováním v odborném hodnocení je nutné dokončit hodnocení pro Minimální sociální záruky (MSS).</li>
        </ul>`,
    cancelButton: "Zrušit",
    startButton: "Začít hodnocení"
}
};

function InstructionPage() {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { examName, examCategory, isMSS, assessmentId } = state || {};

    const currentLanguage = localStorage.getItem('language') || 'english';
    const lang = translations[currentLanguage];

    const startAssessment = () => {
        navigate('/take-assessment', { state: { examName, examCategory, isMSS, assessmentId, edit: false, start: true } });
    };

    const createMarkup = (html) => {
        return { __html: html };
    };

    return (
        <div className='instruction-main container mt-5 py-5'>
            <h5>{lang.welcome}</h5>
            <h3>{examName}</h3><br></br><br></br>
            <p>{lang.instructionIntroduction}</p>

            <div className="instruction-container">
                <p className="instructions-title">{lang.instructionsTitle}</p>
                <div dangerouslySetInnerHTML={createMarkup(lang.instructions)}></div>
            </div>

            <section>
                <button className='btn-cancel' onClick={() => navigate('/landing')}>{lang.cancelButton}</button>
                <button className='btn-Start' onClick={startAssessment}>{lang.startButton}</button>
            </section>
        </div>
    );
}

export default InstructionPage;
